// The shared vocabulary of the two band charts: the tree page's per-tree
// reading and the model page's ensemble reading.
//
// Both draw the same two questions in the same visual language, so the
// formatting and the stacked track live here rather than in either page. They
// were duplicated while the tree page's charts sat in an unmerged PR; that PR
// has landed, and a second copy is how the two surfaces drift apart.
//
// The CSS stays in `treePerformanceCharts.css`, which both already import.
import React from 'react';

import { Threshold } from '../../../models/ModelPerformances';

// Rounded: nobody acts on the second decimal of a lead percentage, and four
// bands each carrying two decimals is unreadable at band width.
export const pct = (value: number) => `${Math.round(value)}%`;
// Rates run small on most fit models, where rounding to whole percent collapses
// the bottom two tiers onto the same number.
export const rate = (value: number) => `${value.toFixed(1)}%`;
export const count = (value: number) => value.toLocaleString('en-US');

// Below this the band is narrower than its own label, and a number sticking out
// of its band is worse than no number: the band is still hoverable for the
// exact figure.
export const MIN_BAND_LABEL = 9;
// Same idea on the precision bars, except the label moves outside the bar
// instead of disappearing, since there are only four of them and each one is
// the point of its row.
export const MIN_INSIDE_LABEL = 24;

/**
 * The only fields the stacked track reads off a tier.
 *
 * Deliberately narrower than either caller's tier type. The tree page's
 * `TierPerformance` requires `population` and `conversions`; the ensemble's tier
 * makes them optional, because payloads cached before the repartition carried
 * absolutes have only the rounded shares. Collapsing those two types would make
 * that fallback unrepresentable, so the shared contract is just what gets drawn.
 */
export interface BandTier {
  /** A fit tier, or an engagement one: the track only keys on it. */
  segment: Threshold | string;
  color: string | null;
  /** Text drawn ON `color`. Never white by default: see nodeSegment's ON_FILL. */
  textColor: string | null;
}

/**
 * One stacked track: every tier as a band, sized by `shareOf`.
 *
 * Generic over the tier so each page keeps its own concrete type in the
 * callbacks rather than widening to `BandTier` and losing the fields it needs.
 */
export function StackedBar<T extends BandTier>({
  label,
  tiers,
  shareOf,
  labelOf,
  alwaysLabel,
  describe,
}: {
  label: string;
  tiers: T[];
  shareOf: (tier: T) => number;
  /**
   * The number to PRINT in a band, when it must differ from the band's width.
   *
   * Rounding each share on its own gives labels that do not total 100: four
   * bands reading 11, 25, 42 and 23 add up to 101, which is the first thing
   * anyone checks. Apportioning by largest remainder fixes the labels but must
   * not move the geometry, so the width stays exact and only the text changes.
   *
   * Optional, so the tree page's charts render exactly as they did.
   */
  labelOf?: (tier: T, index: number) => number;
  /**
   * How many leading bands print their label however narrow they are.
   *
   * `MIN_BAND_LABEL` exists so a sliver does not carry unreadable text, but a
   * caller whose headline is the SUM of the first bands needs those bands to be
   * legible or the two disagree on screen: Very Good 4 and Good 30 gave a
   * headline of 34 over a bar showing 30, 43 and 23.
   */
  alwaysLabel?: number;
  describe: (tier: T) => string;
}) {
  return (
    <div className="tpf-row">
      <span className="tpf-row-label">{label}</span>
      <div className="tpf-track">
        {tiers.map((tier, index) => {
          const value = shareOf(tier);
          const shown = labelOf ? labelOf(tier, index) : value;
          return (
            <span
              key={tier.segment}
              className="tpf-band"
              style={{ width: `${value}%`, background: tier.color }}
              title={describe(tier)}
            >
              {(value >= MIN_BAND_LABEL ||
                (index < (alwaysLabel ?? 0) && shown > 0)) && (
                <span
                  className="tpf-band-value"
                  style={{ color: tier.textColor }}
                >
                  {pct(shown)}
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}
